// Libraries
import React from 'react';
import {css} from 'aphrodite/no-important';
// Core
import CoreMetrics, {createExpandableMediaQueryStylesheet} from '../core/Metrics';
import CoreStyles from '../core/Styles';
import {TitleText, TaglineText, HeadingMediumText, LabelSmallText} from '../core/Type';
// Controls
import Button from '../controls/Button';
// Layouts
import {Grid, OneColumnLayout} from '../layouts/Grid';
import {CssGrid, CssGridRow, CssGridColumn, CssGridSpace} from '../layouts/CssGrid';
import OneContentColumnLayout from '../layouts/OneContentColumnLayout';
// Modules
import CircularTopicIcon from '../modules/CircularTopicIcon';
import SiteBar from '../modules/SiteBar';
import SiteFooter from '../modules/SiteFooter';

export default class HomePage extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            homepage: {
                display: "flex",
                flexDirection: "column-reverse",
            },
        });
        return (
            <div className={css(styles.homepage)}>
                <SiteFooter />
                <DomainList />
                <DomainLinks />
                <HeroModule />
                <SiteBar />
            </div>
        );
    }
}

class HeroModule extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            heroModule: {
                width: "100%",
                backgroundColor: CoreMetrics.Color.LoggedOut.BGBlue,
                background: `linear-gradient(
                    to bottom,
                    ${CoreMetrics.Color.LoggedOut.BGBlue},
                    ${CoreMetrics.Color.LoggedOut.BGGreen}
                )`,
                paddingTop: CoreMetrics.ElementSpacing.XLarge(),
                paddingBottom: CoreMetrics.ElementSpacing.XLarge(),
            },
            heading: {
                // This allows us to center the subheading, which is a label
                // element.
                display: "block",
                textAlign: "center",
                color: CoreMetrics.Color.Neutral.White,
            },
            heroButton: {
                width: "100%",
                marginTop: CoreMetrics.ElementSpacing.Small(),
            },
            buttonBlue: {
                color: CoreMetrics.Color.Neutral.White,
                backgroundColor: CoreMetrics.Color.LoggedOut.DarkBlue,

                ":hover": {
                    backgroundColor: CoreMetrics.Color.LoggedOut.NeutralBlue,
                },
            },
            buttonWhite: {
                backgroundColor: CoreMetrics.Color.Neutral.White,
                color: CoreMetrics.Color.LoggedOut.DarkBlue,

                ":hover": {
                    backgroundColor: CoreMetrics.Color.Neutral.White,
                    color: CoreMetrics.Color.LoggedOut.NeutralBlue,
                },
            },
        });

        const heading = <TitleText styles={[styles.heading]}>
            You can learn anything.
        </TitleText>;
        const subHeading = <TaglineText styles={[styles.heading]}>
            For free. For everyone. Forever.
        </TaglineText>;
        const buttonContainer = <div className={css(styles.buttonContainer)}>
            <Button
                styles={[styles.heroButton, styles.buttonBlue]}
                title="Learners, start here"
            />
            <Button
                styles={[styles.heroButton, styles.buttonBlue]}
                title="Teachers, start here"
            />
            <Button
                styles={[styles.heroButton, styles.buttonWhite]}
                title="Parents, start here"
            />
        </div>;
        const sideSpace = <CssGridSpace
            sizePhone={0}
            sizeTablet={2}
            sizeDesktop={4}
        />;

        return (
            <div className={css(styles.heroModule)}>
                <OneContentColumnLayout column1={heading} />
                <OneContentColumnLayout column1={subHeading} />

                <CssGrid>
                    <CssGridRow>
                        {sideSpace}
                        <CssGridColumn size={4} sizePhone={0}>
                            {buttonContainer}
                        </CssGridColumn>
                        {sideSpace}
                    </CssGridRow>
                </CssGrid>
            </div>
        );
    }
}

class DomainLinks extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            domainLinks: {
                width: "100%",
            },
            domainLinksContainer: {
                textAlign: "center",
                flexWrap: "wrap",
                marginTop: CoreMetrics.ElementPadding.XLarge(),
            },
            domainLink: {
                display: "inline-block",
                whiteSpace: "nowrap",
                marginRight: CoreMetrics.ElementSpacing.XLarge(),
                marginBottom: CoreMetrics.ElementPadding.XLarge(),
            },
        });
        // This component still uses the Grid component because it wouldn't
        // teach us anything to use the css grid layout system.
        return (
            <div className={css(styles.domainLinks, CoreStyles.hairlineOuterStroke)}>
                <OneColumnLayout column1={(
                    <div className={css(styles.domainLinksContainer)}>
                        <LabelSmallText styles={[styles.domainLink]}>Math by subject</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>Math by grade</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>Science &amp; engineering</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>Computing</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>Arts &amp; humanities</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>Economics &amp; finance</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>Test prep</LabelSmallText>
                        <LabelSmallText styles={[styles.domainLink]}>College, careers, &amp; more</LabelSmallText>
                    </div>
                )} />
            </div>
        );
    }
}

class DomainList extends React.Component {
    render() {
        const emptyList = [null, null, null, null];
        return (
            <Grid>
                {emptyList.map((_, index) => <Domain key={`domain${index}`} />)}
            </Grid>
        );
    }
}

class Domain extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            domain: {
                paddingTop: CoreMetrics.ElementPadding.XLarge(),
                paddingBottom: CoreMetrics.ElementPadding.XLarge(),
            },
            domainIconAndTitle: {
                display: "flex",
                alignItems: "center",
                paddingBottom: CoreMetrics.ElementPadding.XLarge(),
            },
            course: {
                // This lets us add margin to the course.
                display: "inline-block",
                marginBottom: CoreMetrics.ElementPadding.Medium(),
            },
            coursePhone: {
                marginLeft: CoreMetrics.ElementSpacing.XLarge(),
            },
        });

        const courseNames = [
            "Early math",
            "Arithmetic",
            "Pre-algebra",
            "Algebra 1",
            "Geometry",
            "Algebra 2",
            "Trigonometry",
            "Precalculus",
            "Statistics & probability",
            "AP Calculus AB",
            "AP Calculus BC",
            "AP Statistics",
            "Multivariable calculus",
            "Differential equations",
            "Linear algebra",
        ];

        const sharedStyles = [styles.domain, CoreStyles.hairlineStrokeBottom];

        return <CssGrid styles={[...sharedStyles]}>
            <CssGridRow>
                <CssGridColumn sizeDesktop={3}>
                    <div className={css(styles.domainIconAndTitle)}>
                        <CircularTopicIcon />
                        <HeadingMediumText styles={[styles.domainTitle]}>
                            Math
                        </HeadingMediumText>
                    </div>
                </CssGridColumn>

                <CssGridColumn size={0} sizeDesktop={9}>
                    <CssGrid size={9} columnsWithin={3}>
                        <CssGridRow>
                            {courseNames.map((courseName, index) =>
                                <CssGridColumn
                                    key={`courseMobile${index}`}
                                    size={0}
                                    sizeDesktop={9}
                                >
                                    <LabelSmallText styles={[styles.course]}>
                                        {courseName}
                                    </LabelSmallText>
                                </CssGridColumn>
                            )}
                        </CssGridRow>
                    </CssGrid>
                </CssGridColumn>
            </CssGridRow>

            {courseNames.map((courseName, index) =>
                <CssGridRow key={`courseDesktop${index}`}>
                    <CssGridColumn sizeDesktop={0}>
                        <LabelSmallText
                            styles={[styles.course, styles.coursePhone]}>
                            {courseName}
                        </LabelSmallText>
                    </CssGridColumn>
                </CssGridRow>
            )}
        </CssGrid>;
    }
}



// WEBPACK FOOTER //
// ./src/demo/HomePage.jsx